import { useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { AppointmentBookingStatus } from "@/domains/appointment/appointments/types/appointment";
import {
  APPOINTMENT_SORT_CONFIG,
  CACHE_POLICIES,
  POLLING_INTERVALS,
  QUERY_LIMITS,
} from "@/lib/config/query-config";
import { useSalonStore } from "@/lib/store/salon";
import { CALENDAR_APPOINTMENTS_QUERY } from "../graphql/calendar";

interface UseCalendarAppointmentsProps {
  startDate: DateTime;
  endDate: DateTime;
  statusFilters?: AppointmentBookingStatus[];
  workerFilters?: string[];
}

export const useCalendarAppointments = ({
  startDate,
  endDate,
  statusFilters = [],
  workerFilters = [],
}: UseCalendarAppointmentsProps) => {
  const { currentSalon } = useSalonStore();

  const { data, loading, error, refetch } = useQuery(CALENDAR_APPOINTMENTS_QUERY, {
    variables: {
      salonId: currentSalon?.id ?? "",
      filters: {
        startDate: startDate.toISO(),
        endDate: endDate.toISO(),
        first: QUERY_LIMITS.APPOINTMENTS_CALENDAR,
        ...APPOINTMENT_SORT_CONFIG.DEFAULT,
        ...(statusFilters.length > 0 && { bookingStatuses: statusFilters }),
        ...(workerFilters.length > 0 && { workerIds: workerFilters }),
      },
    },
    skip: !currentSalon?.id,
    pollInterval: POLLING_INTERVALS.CALENDAR,
    ...CACHE_POLICIES.APPOINTMENTS,
  });

  // Get appointments data from cursor-based response
  const appointments = data?.appointments?.edges.map((edge) => edge.node) ?? [];

  return {
    appointments,
    loading,
    error,
    refetch,
  };
};
